import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { TokenStorageService } from './token-storage.service';
import { AUTH_ERROR_HANDLER } from './auth-error-handler';
import { ShellConfigService } from './shell-config.service';

/**
 * Decides whether the bearer + credentials may be attached to a request.
 *
 * SECURITY (ADO #1543 hardening): we attach the short-lived JWT and the credentialed
 * refresh cookie ONLY to the trusted API origin, derived from the configured
 * `apiUrl` (e.g. `https://test.continity.one/api`). A bare `/api/` substring test
 * is unsafe — an absolute, cross-origin URL whose path merely contains `/api/`
 * would otherwise receive the token. The request URL is resolved against the
 * document origin (so relative URLs are same-origin by construction) and must
 * match BOTH the trusted origin AND the API base path.
 *
 * Degrades safely: if no `ShellConfigService` / `apiUrl` is resolvable (e.g. a
 * minimal test harness), we fall back to the previous behaviour but anchored to a
 * leading-slash relative `/api/` path, which can never be cross-origin.
 */
function targetsTrustedApi(reqUrl: string, apiUrl: string | undefined): boolean {
  // Resolve both the request and the configured apiUrl against the document origin
  // so relative paths ('/api/x') and absolute URLs are compared uniformly.
  const docBase =
    typeof window !== 'undefined' && window.location
      ? window.location.origin
      : undefined;

  let target: URL;
  try {
    target = new URL(reqUrl, docBase);
  } catch {
    return false;
  }

  if (apiUrl) {
    let api: URL;
    try {
      api = new URL(apiUrl, docBase);
    } catch {
      api = undefined as unknown as URL;
    }
    if (api) {
      // Trust requires same origin AND the API base path as a path prefix.
      const basePath = api.pathname.replace(/\/+$/, '');
      return (
        target.origin === api.origin &&
        (target.pathname === basePath ||
          target.pathname.startsWith(`${basePath}/`))
      );
    }
  }

  // Fallback (no resolvable apiUrl): same-origin requests only, path-anchored to /api/.
  return (
    (!docBase || target.origin === docBase) && target.pathname.startsWith('/api/')
  );
}

/**
 * Attaches the in-memory bearer to trusted-API requests and always sends credentials
 * (so the HttpOnly refresh cookie rides along). Mirrors the main portal's
 * authInterceptor, minus the portal-specific 401/403 redirect handling — the
 * consuming app's guard owns access redirects.
 *
 * The bearer + credentials are scoped to the configured API origin (see
 * {@link targetsTrustedApi}) so they can never leak to a third-party host whose
 * path happens to contain `/api/`.
 *
 * 401/403 hook: if an {@link AUTH_ERROR_HANDLER} is provided, each response that
 * fails with status 401 or 403 invokes `handler.handle(err)` and is then RE-THROWN
 * (never swallowed) so existing call-site/error handling is unchanged. With no
 * handler provided, behaviour is byte-identical to having no special handling.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const handler = inject(AUTH_ERROR_HANDLER, { optional: true });
  const apiUrl = inject(ShellConfigService, { optional: true })?.apiUrl;

  // Re-throws untouched; only side-effects via the optional handler on 401/403.
  const withErrorHook = <T>(source: Observable<T>) =>
    handler
      ? source.pipe(
          catchError((err) => {
            if (
              err instanceof HttpErrorResponse &&
              (err.status === 401 || err.status === 403)
            ) {
              handler.handle(err);
            }
            return throwError(() => err);
          })
        )
      : source;

  // Explicit opt-out (used by /Auth/status and /Auth/refresh to avoid a bearer loop).
  if (req.headers.has('X-Skip-Interceptor')) {
    return withErrorHook(
      next(req.clone({ headers: req.headers.delete('X-Skip-Interceptor'), withCredentials: true }))
    );
  }

  if (targetsTrustedApi(req.url, apiUrl)) {
    const token = inject(TokenStorageService).getToken();
    if (token) {
      return withErrorHook(
        next(
          req.clone({
            headers: req.headers.set('Authorization', `Bearer ${token}`),
            withCredentials: true,
          })
        )
      );
    }
    return withErrorHook(next(req.clone({ withCredentials: true })));
  }

  return withErrorHook(next(req));
};
